import type { Key9Puzzle } from '../types/puzzle';
import { getFullSudokuConflicts } from './fullSudoku';

export const CLASSIC_BOARD_GEOMETRY = { width: 430, height: 435, x: 35, y: 35, cellSize: 40 };

// Describe only visible entries and rule conflicts, never the puzzle's solution.
export function getAccessibleCells(puzzle: Key9Puzzle, board: string[], notes: string[], readOnly: boolean) {
  const conflicts = new Set(getFullSudokuConflicts(puzzle, board, new Set()).conflictingUserCells);
  return board.map((value, index) => {
    const given = /^[1-9]$/.test(puzzle.puzzle[index]);
    const note = value === '0' ? notes[index] : '';
    const content = value === '0' ? `Empty${note ? `. Notes ${note.split('').join(', ')}` : ''}` : `${value}. ${given ? 'Given' : 'Your entry'}`;
    return {
      index, row: Math.floor(index / 9) + 1, column: index % 9 + 1,
      readOnly: readOnly || given, conflict: conflicts.has(index),
      label: `Row ${Math.floor(index / 9) + 1}, column ${index % 9 + 1}. ${content}${conflicts.has(index) ? '. Repeated in row, column, or box' : ''}.`
    };
  });
}

export function getGridNavigationTarget(index: number, key: string, control = false): number | null {
  const row = Math.floor(index / 9), col = index % 9;
  if (key === 'Home') return control ? 0 : row * 9;
  if (key === 'End') return control ? 80 : row * 9 + 8;
  if (control) return null;
  if (key === 'ArrowLeft') return row * 9 + Math.max(0, col - 1);
  if (key === 'ArrowRight') return row * 9 + Math.min(8, col + 1);
  if (key === 'ArrowUp') return Math.max(0, row - 1) * 9 + col;
  if (key === 'ArrowDown') return Math.min(8, row + 1) * 9 + col;
  return null;
}

export function getBoardEditAction(event: { key: string; ctrlKey?: boolean; metaKey?: boolean; altKey?: boolean; shiftKey?: boolean; isComposing?: boolean }): 'undo' | 'erase' | 'notes' | number | null {
  if (event.altKey || event.isComposing) return null;
  if (event.ctrlKey || event.metaKey) return event.key.toLowerCase() === 'z' && !event.shiftKey ? 'undo' : null;
  if (event.shiftKey) return null;
  if (/^[1-9]$/.test(event.key)) return Number(event.key);
  if (event.key === 'Backspace' || event.key === 'Delete') return 'erase';
  return event.key.toLowerCase() === 'n' ? 'notes' : null;
}
